import React, { useState, useEffect } from 'react';
import { testUrl } from '../../services/urlService';
import { getSourceUrls } from '../../services/sourceService';
import JsonView from '../common/JsonView';
import { UrlItem } from '../../../../models';
import { JsExtractorPreview } from '../../models/preview';
import { Dialog, Button, Input, Alert, Spinner, Select, SelectOption, Tabs, Tab, Card } from '../ui';

interface TestUrlDialogProps {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  onPreviewReady?: (payload: {
    html?: string;
    sourceUrl: string;
    propertyData?: any;
    jsExtractorResults?: JsExtractorPreview[];
  }) => void;
  initialTestUrl?: string;
}

const TestUrlDialog: React.FC<TestUrlDialogProps> = ({ open, onClose, sourceId, onPreviewReady, initialTestUrl }) => {
  const [testUrlValue, setTestUrlValue] = useState<string>('');
  const [selectedUrlHash, setSelectedUrlHash] = useState<string>('');
  const [existingUrls, setExistingUrls] = useState<UrlItem[]>([]);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    propertyData?: any;
    error?: string;
  } | null>(null);

  // Load existing URLs when dialog opens
  useEffect(() => {
    if (open) {
      loadExistingUrls();
    }
  }, [open, sourceId]);

  useEffect(() => {
    if (!open || !initialTestUrl) {
      return;
    }
    setTestUrlValue((prev) => (prev ? prev : initialTestUrl));
  }, [open, initialTestUrl]);

  const loadExistingUrls = async () => {
    try {
      setLoadingUrls(true);
      const urls = await getSourceUrls(sourceId);
      setExistingUrls(urls);
    } catch (error) {
      console.error('Error loading URLs:', error);
    } finally {
      setLoadingUrls(false);
    }
  };

  const handleUrlSelect = (urlHash: string) => {
    setSelectedUrlHash(urlHash);
    const selectedUrl = existingUrls.find(url => url.urlHash === urlHash);
    if (selectedUrl) {
      setTestUrlValue(selectedUrl.sourceUrl);
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    // Clear the other input when switching tabs
    if (newValue === 0) {
      setSelectedUrlHash('');
    } else {
      setTestUrlValue('');
    }
  };

  const getCurrentUrl = (): string => {
    if (tabValue === 0) {
      return testUrlValue;
    } else {
      const selectedUrl = existingUrls.find(url => url.urlHash === selectedUrlHash);
      return selectedUrl?.sourceUrl || '';
    }
  };

  const runUrlTest = async () => {
    const urlToTest = getCurrentUrl();
    if (!urlToTest.trim()) return;
    
    try {
      setTestLoading(true);
      setTestResult(null);
      const response = await testUrl(sourceId, urlToTest);
      const successResult = {
        success: true,
        message: response.message,
        propertyData: response.propertyData
      };
      setTestResult(successResult);
      onPreviewReady?.({
        html: response.html,
        sourceUrl: urlToTest,
        propertyData: response.propertyData,
        jsExtractorResults: response.jsExtractorResults
      });
    } catch (err: any) {
      console.error('Error testing URL:', err);
      setTestResult({
        success: false,
        message: 'Failed to test URL',
        error: err.response?.data?.message || 'Unknown error occurred'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleClose = () => {
    setTestUrlValue('');
    setSelectedUrlHash('');
    setTestResult(null);
    setTabValue(0);
    onClose();
  };

  const canTest = () => {
    if (tabValue === 0) {
      return testUrlValue.trim().length > 0;
    } else {
      return selectedUrlHash.length > 0;
    }
  };

  const tabs: Tab[] = [
    { label: 'Type URL' },
    { label: 'Select from Existing URLs' }
  ];

  const urlOptions: SelectOption[] = existingUrls.map(url => ({
    value: url.urlHash,
    label: url.sourceUrl
  }));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Test URL with Source Parser"
      maxWidth="2xl"
      footer={
        <Button variant="outline" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <Tabs tabs={tabs} activeTab={tabValue} onChange={handleTabChange} />

        {/* Type URL Tab */}
        {tabValue === 0 && (
          <div className="pt-4">
            <Input
              label="URL to Test"
              value={testUrlValue}
              onChange={(e) => setTestUrlValue((e.target as HTMLInputElement).value)}
              fullWidth
              placeholder="Enter a URL to test with this source's parsing configuration"
            />
          </div>
        )}

        {/* Select from Existing URLs Tab */}
        {tabValue === 1 && (
          <div className="pt-4">
            {loadingUrls ? (
              <div className="flex justify-center py-4">
                <Spinner size="md" />
              </div>
            ) : (
              <Select
                value={selectedUrlHash}
                label="Select URL"
                options={urlOptions}
                onChange={(e) => handleUrlSelect(e.target.value)}
                fullWidth
              />
            )}

            {selectedUrlHash && (
              <div className="mt-3">
                <p className="text-sm text-gray-600">
                  Selected URL: {getCurrentUrl()}
                </p>
              </div>
            )}
          </div>
        )}

        <hr className="my-4 border-gray-200" />

        <Button
          variant="primary"
          onClick={runUrlTest}
          disabled={testLoading || !canTest()}
          className="mb-4"
        >
          {testLoading && <Spinner size="sm" className="mr-2" />}
          {testLoading ? 'Testing...' : 'Test URL'}
        </Button>

        {testResult && (
          <div className="mt-4">
            <Alert
              variant={testResult.success ? 'success' : 'error'}
              className="mb-4"
            >
              {testResult.message}
              {testResult.error && (
                <p className="text-sm mt-2">
                  Error: {testResult.error}
                </p>
              )}
            </Alert>

            {testResult.propertyData && (
              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Extracted Property Data:
                </p>
                <Card>
                  <div className="p-4">
                    <JsonView data={testResult.propertyData} />
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default TestUrlDialog;
